import { useCallback, useEffect, useRef, useState } from 'react';
import { Storage } from '../lib/storage';
import { resultFingerprint } from './resultFingerprint';
import { unsavedScoreIds } from './unsavedScores';

// ─────────────────────────────────────────────────────────────────────
// THE PORTAL LEARNS THAT A BANEPAD CHANGED THE TOURNAMENT (#556).
//
// A pad's result was applied and persisted, and this browser never found out:
// `useTournaments` reads `active_tournament_v1` once and there was no poll, no
// focus refresh, nothing. So «Kamper» showed the match unplayed, the leaderboard
// was computed from a copy that predated it, and re-entering the result got
// «allerede registrert» from a server that had it all along.
//
// Nothing was lost — writes are conditional, so a stale write is refused rather
// than applied — but the screen was simply wrong, and the operator had no way to
// know which of the two was.
//
// ── WHAT IT MAY TAKE SILENTLY, AND WHAT IT MUST ASK ABOUT ───────────
//
// Most incoming changes are another court's business and are adopted with no
// interruption. One that touches a match somebody is TYPING INTO is not: the
// adoption would discard what they entered, which is the failure #537 exists
// for. That one raises a banner and changes nothing until they say so.
//
// CONSERVATIVE WHERE IT CANNOT TELL. If the incoming document cannot be diffed —
// not an object, no match array, matches without ids — the answer is «ask»,
// never «adopt». Being wrong towards a banner costs a click; being wrong the
// other way costs somebody's work, silently, which is exactly what makes that
// asymmetry worth spelling out.
// ─────────────────────────────────────────────────────────────────────

/** How often the portal asks, while a view that shows pad results is open. */
export const TOURNAMENT_REFRESH_MS = 10_000;

const ACTIVE_TOURNAMENT_KEY = 'active_tournament_v1';

type Match = { id?: unknown };

/**
 * One comparable fingerprint per match — what a result changes, nothing else.
 *
 * SHARED WITH THE SCORE ROWS (#559 review). This used to build its own
 * `score1:score2:completed` string, which ignored `match.score` — where a KM or
 * seriespill result actually lives. A corrected KM score with the same set
 * counts was therefore «no change», and the portal stayed stale with nothing to
 * notice it.
 */
function resultsById(session: unknown): Map<string, string> | null {
  if (!session || typeof session !== 'object') return null;
  const matches = (session as { matches?: unknown }).matches;
  if (!Array.isArray(matches)) return null;
  const out = new Map<string, string>();
  for (const raw of matches) {
    const m = (raw ?? {}) as Match;
    const id = String(m.id ?? '');
    // A match with no id cannot be compared. Reported as undiffable rather than
    // skipped — skipping it would call an unknown change «no change».
    if (!id) return null;
    out.set(id, resultFingerprint(m));
  }
  return out;
}

interface Diff {
  /** Ids whose result differs. */
  changed: Set<string>;
  /**
   * The match SET differs — one appeared or vanished.
   *
   * Tracked apart from `changed` because it means something different: a score
   * arriving is one match being recorded, while the set changing means the round
   * was rebuilt. An edit in progress may then be for a match that no longer
   * exists, or no longer means what it did, so this is not judged by whether the
   * edited id happens to be among the ids that moved.
   */
  structural: boolean;
}

/** What differs between the two documents, or null if it cannot be read. */
function diffResults(mine: unknown, theirs: unknown): Diff | null {
  const a = resultsById(mine);
  const b = resultsById(theirs);
  if (!a || !b) return null;
  const changed = new Set<string>();
  for (const [id, fingerprint] of b) if (a.get(id) !== fingerprint) changed.add(id);
  let structural = false;
  for (const id of a.keys()) {
    if (!b.has(id)) {
      changed.add(id);
      structural = true;
    }
  }
  for (const id of b.keys()) if (!a.has(id)) structural = true;
  return { changed, structural };
}

export interface TournamentRefresh {
  /** A change is waiting that would touch unsent input. Null when there is none. */
  pending: { changed: string[] } | null;
  /** Take the waiting change now — the banner's button. */
  accept: () => void;
  /** Ask the server once, right now. */
  check: () => void;
}

export interface TournamentRefreshDeps {
  /** What this client currently holds, or null. */
  tournament: unknown;
  /**
   * Adopt a newer document.
   *
   * NO VERSION ARGUMENT, because there is nothing to pass: `Storage.read`
   * records the server's version for the key as it reads, so this client's next
   * conditional write already carries the right one. Threading it by hand would
   * be a second copy of a token that is only correct in one place.
   */
  onAdopt: (next: unknown) => void;
  /** Poll while true — a view that shows pad results is open. */
  live?: boolean;
  pollMs?: number;
  /** Injectable for tests. */
  read?: typeof Storage.read;
}

export function useTournamentRefresh({
  tournament,
  onAdopt,
  live = false,
  pollMs = TOURNAMENT_REFRESH_MS,
  read,
}: TournamentRefreshDeps): TournamentRefresh {
  const [pending, setPending] = useState<{ next: unknown; changed: string[] } | null>(null);
  // Held in refs so a new callback identity from the parent cannot retrigger a
  // read — the same separation `usePadResultSubmission` documents at length.
  const mine = useRef(tournament);
  mine.current = tournament;
  const adopt = useRef(onAdopt);
  adopt.current = onAdopt;

  const check = useCallback(async () => {
    const reader = read ?? Storage.read;
    let res: Awaited<ReturnType<typeof Storage.read>>;
    try {
      res = await reader(ACTIVE_TOURNAMENT_KEY);
    } catch {
      return; // an outage is the server's problem to recover from, not ours
    }
    // `absent` and `failed` are both «no usable answer», and they are different
    // facts (#413) — but neither is a reason to throw away what is on screen.
    // Adopting an absent tournament would blank a running one on a wifi blink.
    if (res.status !== 'ok' || !res.value) return;

    let next: unknown;
    try {
      next = JSON.parse(res.value);
    } catch {
      return;
    }

    const diff = diffResults(mine.current, next);
    if (diff && diff.changed.size === 0 && !diff.structural) {
      // Nothing differs. Adopting anyway would replace object identities for no
      // reason and remount half the screen mid-round.
      setPending(null);
      return;
    }

    const atRisk = unsavedScoreIds();
    // THREE REASONS TO ASK RATHER THAN ADOPT, and all three are «we might
    // destroy work»:
    //
    //   • the diff is unreadable, so we cannot know what would be lost;
    //   • a match somebody is typing into has changed underneath them;
    //   • the match SET changed while somebody is typing — the round was
    //     rebuilt, and their edit may be for a match that no longer exists.
    //
    // With nothing at risk, none of them apply and the change is taken silently.
    const collides =
      atRisk.size > 0 &&
      (!diff || diff.structural || [...diff.changed].some((id) => atRisk.has(id)));
    if (collides) {
      setPending({ next, changed: diff ? [...diff.changed] : [] });
      return;
    }
    setPending(null);
    adopt.current(next);
  }, [read]);

  const accept = useCallback(() => {
    setPending((p) => {
      if (p) adopt.current(p.next);
      return null;
    });
  }, []);

  // ON FOCUS, ALWAYS. Somebody coming back to the portal after watching a court
  // is the moment a pad's result is most likely to be waiting, and it costs one
  // read rather than one every ten seconds forever.
  useEffect(() => {
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [check]);

  // AND ON A POLL, but only while a view that shows pad results is open. Polling
  // a tournament document for every portal user all day would be paid for by
  // every club, to answer a question most screens are not asking.
  useEffect(() => {
    if (!live) return undefined;
    void check();
    const id = setInterval(() => void check(), pollMs);
    return () => clearInterval(id);
  }, [live, pollMs, check]);

  return {
    pending: pending ? { changed: pending.changed } : null,
    accept,
    check: () => void check(),
  };
}
